import { Application } from '@platform/client';
import { SAPApplicationParameters } from '@platform/client/schemas';
import { RadioGroup } from '@platform/components';
import { SubmitHandler, useForm } from 'react-hook-form';
import { ApplicationPageProps } from '../../Application';
import { useApplicationLocalStorage } from '../../application-hook';
import { ApplicationBackground } from '../../ApplicationBackground';
import { ApplicationPageContent } from '../../components/ApplicationPageContent';
import BackgroundImage from '../../images/Screen2.png';

type FormValues = {
  cohort: number;
};

const dateOptions = {
  month: 'short',
  day: 'numeric',
} as Intl.DateTimeFormatOptions;

export const SAPCohortSelection = (props: ApplicationPageProps) => {
  const { onBack, onContinue, progress, dataKey } = { ...props };

  const { updateApplicationData, getApplicationDataByKey } =
    useApplicationLocalStorage<SAPApplicationParameters>(dataKey);

  const cohorts = Application.useGetAvailableCohortsSap().data || [];

  const cohortItems = cohorts.map((cohort) => {
    const startDate = cohort.startDate ? new Date(cohort.startDate) : undefined;
    const endDate = cohort.endDate ? new Date(cohort.endDate) : undefined;

    return {
      value: cohort.cohortId.toString(),
      title: cohort.displayName,
      description: cohort.description,
      pillContent:
        startDate && endDate ?
          `${startDate.toLocaleDateString('en-US', dateOptions)} - ${endDate.toLocaleDateString('en-US', dateOptions)}`
        : undefined,
    };
  });

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
  } = useForm<FormValues>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: { cohort: getApplicationDataByKey('cohort') },
  });

  const onChange = (value?: string | number) => {
    setValue('cohort', value as number);
  };

  const submit: SubmitHandler<FormValues> = (data) => {
    updateApplicationData({ cohort: data.cohort });
    onContinue();
  };

  const handleBack = () => {
    updateApplicationData({
      cohort: watch('cohort'),
    });
    onBack();
  };

  return (
    <ApplicationBackground
      progress={progress}
      onBack={handleBack}
      onSubmit={handleSubmit(submit)}
      backgroundImage={BackgroundImage}
    >
      <ApplicationPageContent
        title='Which cohort would you like to join?'
        description={
          <div>
            Don't see a specific cohort?{' '}
            <a
              href='https://movingworlds.org/partner/sap/closed-cohorts-info'
              target='_blank'
              className='text-brand-500 underline'
            >
              Learn more
            </a>{' '}
          </div>
        }
      >
        <div className='pb-8'>
          <RadioGroup
            name='sap-cohort-selection'
            aria-label='sap-cohort-selection'
            register={() => {
              return register('cohort', {
                required: 'Select a cohort to continue',
              });
            }}
            alignStart={true}
            pillProps={{
              color: 'gray',
              size: 'md',
              variant: 'plain',
            }}
            error={errors.cohort?.message}
            items={cohortItems}
            defaultValue={getApplicationDataByKey('cohort')}
            onChange={onChange}
          />
        </div>
      </ApplicationPageContent>
    </ApplicationBackground>
  );
};
